'use client';
import React from 'react';
import classNames from 'classnames';
import { YouTubeEmbed } from '@next/third-parties/google';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';

import styled from '@benzinga/themetron';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import Hooks from '@benzinga/hooks';
import benzingaPhoneAlertsBackground from './assets/benzingaPhoneAlertsBackground.png';

import { bellIcon, bellIconSmall, successIcon } from './assets/icons';
import { HubspotForm } from './HubspotForm';
import { AnalyticsContext } from '@benzinga/analytics';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';
import { BeehiivForm } from './BeehiivForm';

export interface FieldType {
  componentType: string;
  icon: IconDefinition;
  name: string;
  placeholder: string;
  type: string;
}
export interface CallToActionFormProps {
  beehiivFormId?: string;
  hubspotFormId?: string;
  onSubmit?: () => void;
  subtitle?: string;
  successMessage?: string;
  title?: string;
  videoID?: string;
  image?: string;
  border?: boolean;
  buttonHref?: string;
  buttonText?: string;
  flat?: boolean;
  rounded?: boolean;
  redirectUrl?: string;
  extraFields?: FieldType[];
  styleOption?: StyleOption;
  fullWidth?: boolean;
  compact?: boolean;
}

export type StyleOption = 'default' | 'compact' | 'secondary' | 'secondary-article-india';

export const CallToActionForm = ({
  beehiivFormId = '',
  border,
  buttonHref,
  buttonText,
  compact = false,
  extraFields,
  flat = false,
  fullWidth = false,
  hubspotFormId,
  image,
  onSubmit,
  redirectUrl,
  rounded = true,
  styleOption = 'default',
  subtitle,
  successMessage,
  title,
  videoID,
}: CallToActionFormProps) => {
  const session = React.useContext(SessionContext);
  const [success, setSuccess] = React.useState(false);
  const [responseSuccessMessage, setResponseSuccessMessage] = React.useState<string>('');
  const { width } = Hooks.useWindowSize();

  const analytics = React.useContext(AnalyticsContext);

  const handleSuccess = React.useCallback(
    (message: string) => {
      setSuccess(true);
      setResponseSuccessMessage(message);
      const type =
        hubspotFormId !== '' && hubspotFormId !== undefined
          ? 'hubspot'
          : beehiivFormId != '' && beehiivFormId !== undefined
            ? 'beehiiv'
            : '';

      const formId =
        hubspotFormId !== '' && hubspotFormId !== undefined
          ? hubspotFormId
          : beehiivFormId != '' && beehiivFormId !== undefined
            ? beehiivFormId
            : '';

      session
        .getManager(TrackingManager)
        .trackFormEvent('submit', formId || title || analytics?.label || type + '_email_capture', {
          form_type: type + '_email_capture',
        });

      if (onSubmit) {
        onSubmit();
      }
    },
    [beehiivFormId, analytics?.label, hubspotFormId, onSubmit, session, setSuccess, title],
  );

  const message =
    successMessage || responseSuccessMessage || 'Congratulations! <br/> You have successfully subscribed.';

  const imageUrl: string = (benzingaPhoneAlertsBackground as unknown as { src: string })?.src;

  if (!videoID && !image && !buttonText && !title && !subtitle && !beehiivFormId && !hubspotFormId) {
    return null;
  }

  switch (styleOption) {
    case 'default':
      return (
        <Container
          border={border}
          className={`call-to-action-container ${fullWidth ? 'full' : ''}`}
          flat={flat}
          rounded={rounded}
        >
          <div className="media-container">
            {videoID && (
              <div className="video-container">
                <YouTubeEmbed videoid={videoID} />
              </div>
            )}
            {image && (
              <div className="image-container">
                <img alt={title} src={image} />
              </div>
            )}
          </div>
          <div className="h-full">
            {title && (
              <div className="title">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
              </div>
            )}
            {subtitle && (
              <div className="subtitle">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(subtitle) }} />
              </div>
            )}
            {buttonText ? (
              <div>
                <CallToActionLink className="cta-link-button" href={buttonHref}>
                  {buttonText}
                </CallToActionLink>
              </div>
            ) : (
              (!!beehiivFormId || !!hubspotFormId) && (
                <div className="success-message-container">
                  {success && (
                    <div className="success-message-text">
                      <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
                    </div>
                  )}
                  {!success && beehiivFormId && (
                    <BeehiivForm onSuccess={handleSuccess} pub_id={beehiivFormId} redirectUrl={redirectUrl} />
                  )}
                  {!success && hubspotFormId && (
                    <HubspotForm
                      extraFields={extraFields ? extraFields : undefined}
                      hubspotFormId={hubspotFormId}
                      onSuccess={handleSuccess}
                      redirectUrl={redirectUrl}
                    />
                  )}
                </div>
              )
            )}
          </div>
        </Container>
      );
    case 'compact':
      return (
        <CompactContainer>
          {title && (
            <div className="title">
              <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
            </div>
          )}
          {subtitle && (
            <div className="subtitle">
              <span className="mt-4" dangerouslySetInnerHTML={{ __html: sanitizeHTML(subtitle) }} />
            </div>
          )}
          <div className="icon">{bellIconSmall}</div>
          <div className="secondary-container-input-wrapper">
            {beehiivFormId && (
              <div className={classNames('secondary-container-hubspot-form-wrapper', { [`field-active`]: !success })}>
                <BeehiivForm
                  onSuccess={handleSuccess}
                  placeholderTitle="Try it today"
                  pub_id={beehiivFormId}
                  redirectUrl={redirectUrl}
                  styleOption="secondary"
                />
              </div>
            )}
            {hubspotFormId && (
              <div className={classNames('secondary-container-hubspot-form-wrapper', { [`field-active`]: !success })}>
                <HubspotForm
                  extraFields={extraFields ? extraFields : undefined}
                  hubspotFormId={hubspotFormId}
                  onSuccess={handleSuccess}
                  placeholderTitle="Try it today"
                  redirectUrl={redirectUrl}
                  styleOption="secondary"
                />
              </div>
            )}
            <div className={classNames('secondary-container-success-message', { [`field-active`]: success })}>
              <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
            </div>
          </div>
        </CompactContainer>
      );
    case 'secondary':
    case 'secondary-article-india':
      return (
        <SecondaryContainer
          className={classNames('secondary-container', {
            'is-india-app-article': styleOption === 'secondary-article-india',
            'secondary-container-compact': compact,
            'secondary-container-full-width': fullWidth && width >= 568,
          })}
          fullWidth={fullWidth}
        >
          <div
            className="secondary-container-image-wrapper"
            role="img"
            style={{
              backgroundImage: `url(${image || imageUrl})`,
            }}
          >
            <div className="secondary-container-icons-wrapper">
              <div className={classNames('secondary-container-icon', { [`icon-active`]: !success })}>{bellIcon}</div>
              <div className={classNames('secondary-container-icon', { [`icon-active`]: success })}>{successIcon}</div>
            </div>
          </div>
          <div className="secondary-container-content-wrapper">
            {title && (
              <div aria-level={1} className="secondary-container-title" role="heading">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
              </div>
            )}
            {subtitle && (
              <div aria-level={2} className="secondary-container-subtitle" role="heading">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(subtitle) }} />
              </div>
            )}
            <div className="secondary-container-input-wrapper">
              {beehiivFormId && (
                <BeehiivForm
                  onSuccess={handleSuccess}
                  placeholderTitle="Try it today"
                  pub_id={beehiivFormId}
                  styleOption="secondary"
                />
              )}
              {hubspotFormId && (
                <div className={classNames('secondary-container-hubspot-form-wrapper', { [`field-active`]: !success })}>
                  <HubspotForm
                    extraFields={extraFields ? extraFields : undefined}
                    hubspotFormId={hubspotFormId}
                    onSuccess={handleSuccess}
                    placeholderTitle="Try it today"
                    redirectUrl={redirectUrl}
                    styleOption="secondary"
                  />
                </div>
              )}
              {success && (
                <div className={classNames('secondary-container-success-message', { [`field-active`]: success })}>
                  <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
                </div>
              )}
            </div>
          </div>
        </SecondaryContainer>
      );
    default:
      return null;
  }
};

const Container = styled.div<{ border?: boolean; flat?: boolean; rounded?: boolean }>`
  ul {
    margin-left: 1.5rem;
    list-style: disc;
  }

  &.call-to-action-container {
    /* bg-white border border-gray-400 className="hover:shadow-lg" flex flex-col p-4 rounded-md shadow-md space-y-4 */

    display: flex;
    flex-direction: column;
    border-width: ${({ flat }) => (flat ? '0' : '1px')};
    background-color: #ffffff;
    box-shadow: ${({ flat, theme }) => (flat ? 'none' : theme.shadow.md)};
    border-color: rgb(156, 163, 175);
    border-radius: ${({ rounded }) => (rounded ? '0.375rem' : '0')};

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.lg};
    }

    > div:last-of-type {
      display: flex;
      flex-direction: column;
      padding: 1rem;

      > div {
        margin-bottom: 0.5rem;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }

    .media-container {
    }

    .video-container {
      position: relative;
      padding-bottom: 56.25%;
      height: 0;
      overflow: hidden;
      max-width: 100%;

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }

    .image-container {
      width: 100%;
      img {
        width: 100%;
      }
    }

    .title {
      font-size: 1.125rem;
      line-height: 1.75rem;
      font-weight: 700;
      border-color: ${({ theme }) => theme.colorPalette.blue600};
      border-bottom-width: ${props => (props.border ? 8 : 0)}px;
      text-transform: uppercase;
      padding-bottom: ${props => (props.border ? 10 : 0)}px;
    }

    .subtitle {
      font-size: 0.875rem;
      line-height: 1.25rem;
      margin-top: 5px;
      margin-bottom: 15px;
    }

    .success-message-container {
      display: flex;
      justify-content: center;

      .success-message-text {
        color: ${({ theme }) => theme.colorPalette.green400};
        font-size: 14px;
      }

      > div {
        font-weight: 700;
      }
    }
  }
`;

const CallToActionLink = styled.a`
  display: inline-block;
  height: 2.5rem;
  width: max-content;
  white-space: nowrap;
  border-radius: 0.25rem;
  background-color: #002d45;
  padding: 0 0.875rem;
  text-align: center;
  font-family: sans-serif;
  font-size: 0.875rem;
  //line-height: 1.25rem;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 2.5rem;
  letter-spacing: 0.1em;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadow.sm};

  &:hover {
    background-color: rgb(217, 105, 0);
    box-shadow: ${({ theme }) => theme.shadow.md};
    position: relative;
    bottom: 2px;
  }
`;
const CompactContainer = styled.div`
  width: 100%;
  height: 125px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  background: #24344f;

  .title {
    width: 280px;
    height: 24px;
    flex-direction: column;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
    font-size: 15px;
    font-style: normal;
    font-weight: 800;
    line-height: 22px;
    text-transform: uppercase;
    margin-top: 12px;
    margin-left: 16px;
  }
  .subtitle {
    display: flex;
    width: 280px;
    height: 19px;
    flex-direction: column;
    justify-content: center;
    flex-shrink: 0;
    color: var(--Light-Blue-15---95, #ceddf2);
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    margin-left: 16px;
    margin-top: 2px;
  }
  .icon {
    position: relative;
    top: -45px;
    right: -293px;
  }
  .secondary-container-input-wrapper {
    margin-top: -32px;
    height: 40px;
    padding: 0 15px;
  }

  .secondary-container-hubspot-form-wrapper {
    transition: 0.25s transform ease-in-out;
    transform: scale(0);
  }

  .secondary-container-success-message {
    transition: 0.25s transform ease-in-out;
    transform: scale(0);
    background: #ffffff;
    border-radius: 4px;
    font-weight: 700;
    font-size: 15px;
    line-height: 41px;
    color: #3f83f8;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -41px;
  }

  .field-active {
    transform: scale(1);
  }
`;

const SecondaryContainer = styled.div<{ fullWidth?: boolean }>`
  cursor: default;

  &.secondary-container {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '550px')};
    background-color: #121d2e;
    border-radius: ${({ fullWidth }) => (fullWidth ? '0' : '4px')};
    overflow: hidden;
    min-height: 340px;

    &.is-india-app-article {
      border-radius: 8px;
      .secondary-container-image-wrapper {
        height: 125px;
      }

      .secondary-container-title {
        line-height: 32px;
      }

      .secondary-container-subtitle {
        font-size: 14px;
      }
    }

    .secondary-container-image-wrapper {
      position: relative;
      max-width: 568px;
      min-width: 184px;
      height: 112px;
      min-height: 112px;
      background-position: top center;
      background-size: 568px;
      background-repeat: no-repeat;
    }

    .secondary-container-icons-wrapper {
      position: absolute;
      top: 48px;
      left: 50%;
      transform: translateX(-50%);
      width: 72px;
      height: 72px;

      &.is-india-app-article {
        top: 48px;
      }
    }

    .secondary-container-icon {
      position: absolute;
      top: 0;
      left: 0;
      transition: 0.25s transform ease-in-out;
      transform: scale(0);
    }

    .icon-active {
      transform: scale(1);
    }

    .secondary-container-content-wrapper {
      padding: 12px 16px 16px;
      background-image: linear-gradient(180deg, #24344f 0%, rgba(21, 36, 50, 0) 59.98%);
      background-size: 100% 168px;
      background-repeat: no-repeat;
      background-position: top;
      z-index: 1;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    .secondary-container-title {
      margin-bottom: 8px;
      font-weight: 800;
      font-size: 22px;
      line-height: 28px;
      text-align: center;
      text-transform: uppercase;
      color: #ffffff;

      &.is-india-app-article {
        line-height: 32px;
      }
    }

    .secondary-container-subtitle {
      margin-bottom: 16px;
      font-weight: 500;
      font-size: 14px;
      line-height: 22px;
      text-align: center;
      color: #ceddf2;

      &.is-india-app-article {
        font-size: 14px;
      }
    }

    .secondary-container-hubspot-form-wrapper {
      transition: 0.25s transform ease-in-out;
    }

    .secondary-container-success-message {
      width: 100%;
      height: 100%;
      transition: 0.25s transform ease-in-out;
      background: ${({ theme }) => theme.colorPalette.green200};
      color: ${({ theme }) => theme.colorPalette.green500};
      border: 1px solid ${({ theme }) => theme.colorPalette.green500};
      border-radius: 4px;
      font-weight: 700;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      margin-top: 5px;
    }

    .field-active {
      transform: scale(1);
    }

    &.secondary-container-full-width {
      display: flex;
      flex-direction: row-reverse;
      align-items: stretch;

      .secondary-container-image-wrapper {
        height: unset;
        flex: 1 1;
      }

      .secondary-container-icons-wrapper {
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .secondary-container-content-wrapper {
        padding: 24px;
        flex: 1 0;
        background-image: linear-gradient(270deg, #24344f 0%, rgba(21, 36, 50, 0) 59.98%);
        background-size: 168px 100%;
        background-repeat: no-repeat;
        background-position: right;
      }

      .secondary-container-title {
        text-align: unset;
        width: 288px;
      }

      .secondary-container-subtitle {
        text-align: unset;
        width: 288px;
      }
    }

    &.secondary-container-compact {
      width: 100%;
      max-width: 100%;
      min-height: 0;
      border-radius: 4px;

      .secondary-container-image-wrapper {
        height: 90px;
        min-height: 90px;
        background-size: 300px;
      }

      .secondary-container-icons-wrapper {
        top: 36px;
        width: 56px;
        height: 56px;
      }

      .secondary-container-icon svg {
        width: 56px;
        height: 56px;
      }

      .secondary-container-content-wrapper {
        padding: 11px 14px 14px;
        background-size: 100% 140px;
      }

      .secondary-container-title {
        margin-bottom: 7px;
        font-size: 17px;
        line-height: 22px;
        letter-spacing: 0.01em;
      }

      .secondary-container-subtitle {
        margin-bottom: 14px;
        font-size: 13px;
        line-height: 18px;
      }
    }
  }
`;
